import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdRefresh, MdAutorenew, MdVisibility } from "react-icons/md";
import { createApi } from "../../../../core/api";

type ListingCategory =
  | "AIRCRAFT_SALE_LEASE_AVAILABLE"
  | "AIRCRAFT_SALE_LEASE_WANTED"
  | "WET_LEASE_AVAILABLE"
  | "WET_LEASE_WANTED"
  | "ENGINE_AVAILABLE"
  | "ENGINE_WANTED"
  | "CHARTER_AVAILABLE"
  | "CHARTER_WANTED"
  | "BUSINESS_JET_AVAILABLE"
  | "BUSINESS_JET_WANTED";

interface ExpiredListing {
  id: number;
  category: ListingCategory;
  categoryLabel: string;
  moduleGroup: "Aircraft" | "Wet Lease" | "Engine" | "Charter" | "Business Jet";
  title: string;
  subTitle?: string;
  companyName?: string;
  userName?: string;
  createdDate?: string;
  raw: any;
}

const EXPIRED = 3; // ConfirmStatus.Expired

export const ExpiredListingsPage: React.FC = () => {
  const { setPageMenus } = useOutletContext<any>();
  const api = useMemo(() => createApi(), []);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [listings, setListings] = useState<ExpiredListing[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [confirmRenew, setConfirmRenew] = useState<ExpiredListing | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<ExpiredListing | null>(null);

  const extractData = (r: any) => (r?.data?.data ?? r?.data ?? []).filter((x: any) => x.confirmStatus === EXPIRED);

  const loadExpired = useCallback(async () => {
    setLoading(true);
    try {
      const results: ExpiredListing[] = [];

      const sources: Array<{
        call: () => Promise<any>;
        category: ListingCategory;
        categoryLabel: string;
        moduleGroup: ExpiredListing["moduleGroup"];
        titleOf: (x: any) => string;
        subOf?: (x: any) => string | undefined;
      }> = [
        {
          call: () => api.aircraftsaleleaseAvailable.getAll(),
          category: "AIRCRAFT_SALE_LEASE_AVAILABLE",
          categoryLabel: "Aircraft Sale/Lease Available",
          moduleGroup: "Aircraft",
          titleOf: (x) => `${x.aircraftType?.name ?? "Aircraft"} (${x.tradeType === 1 ? "Sale" : "Lease"})`,
          subOf: (x) => `MSN: ${x.msn ?? "-"} | YOM: ${x.yom ?? "-"}`,
        },
        {
          call: () => api.aircraftsaleleaseWanted.getAll(),
          category: "AIRCRAFT_SALE_LEASE_WANTED",
          categoryLabel: "Aircraft Sale/Lease Wanted",
          moduleGroup: "Aircraft",
          titleOf: (x) => `${x.aircraftType?.name ?? "Aircraft Wanted"} (${x.tradeType === 1 ? "Buy" : "Lease"})`,
          subOf: (x) => `Preferred YOM: ${x.minYOM ?? "-"} - ${x.maxYOM ?? "-"}`,
        },
        {
          call: () => api.wetLeaseAvailable.getAll(),
          category: "WET_LEASE_AVAILABLE",
          categoryLabel: "Wet Lease Available",
          moduleGroup: "Wet Lease",
          titleOf: (x) => `${x.aircraftType?.name ?? "Wet Lease"} Available`,
          subOf: (x) => `MSN: ${x.msn ?? "-"} | Base: ${x.baseAirport?.iataCode ?? "-"}`,
        },
        {
          call: () => api.wetLeaseWanted.getAll(),
          category: "WET_LEASE_WANTED",
          categoryLabel: "Wet Lease Wanted",
          moduleGroup: "Wet Lease",
          titleOf: (x) => `${x.aircraftType?.name ?? "Wet Lease"} Wanted`,
          subOf: (x) => `Routing: ${x.originAirport?.iataCode ?? "-"} -> ${x.destinationAirport?.iataCode ?? "-"}`,
        },
        {
          call: () => (api as any).engineSaleLeaseAvailable.getAll({ isAll: true }),
          category: "ENGINE_AVAILABLE",
          categoryLabel: "Engine Available",
          moduleGroup: "Engine",
          titleOf: (x) => `${x.engineType?.name ?? "Engine"} (${x.tradeType === 1 ? "Sale" : "Lease"})`,
          subOf: (x) => `ESN: ${x.esn ?? "-"} | Location: ${x.airport?.iataCode ?? "-"}`,
        },
        {
          call: () => (api as any).engineSaleLeaseWanted.getAll({ isAll: true }),
          category: "ENGINE_WANTED",
          categoryLabel: "Engine Wanted",
          moduleGroup: "Engine",
          titleOf: (x) => `${x.engineType?.name ?? "Engine Wanted"}`,
          subOf: (x) =>
            `Target Date: ${x.deliveryDate ? new Date(x.deliveryDate).toLocaleDateString() : "-"}`,
        },
        {
          call: () => (api as any).aircraftCharterAvailable.getAll({ isAll: true }),
          category: "CHARTER_AVAILABLE",
          categoryLabel: "Charter Available",
          moduleGroup: "Charter",
          titleOf: (x) => `${x.aircraftType?.name ?? "Charter Aircraft"} Available`,
          subOf: (x) => `Capacity: ${x.capacity ?? "-"} Seats`,
        },
        {
          call: () => (api as any).aircraftCharterWanted.getAll({ isAll: true }),
          category: "CHARTER_WANTED",
          categoryLabel: "Charter Wanted",
          moduleGroup: "Charter",
          titleOf: (x) => `Charter Wanted: ${x.aircraftType?.name ?? "Aircraft"}`,
          subOf: (x) =>
            `Pax: ${x.paxCount ?? "-"} | Route: ${x.originAirport?.iataCode ?? "-"} -> ${x.destinationAirport?.iataCode ?? "-"}`,
        },
        {
          call: () => (api as any).businessJetAvailable.getAll({ isAll: true }),
          category: "BUSINESS_JET_AVAILABLE",
          categoryLabel: "Business Jet Available",
          moduleGroup: "Business Jet",
          titleOf: (x) => `${x.aircraftType?.name ?? "Business Jet"} Available`,
          subOf: (x) => `Base: ${x.airport?.iataCode ?? "-"} | Pax: ${x.paxCapacity ?? "-"}`,
        },
        {
          call: () => (api as any).businessJetWanted.getAll({ isAll: true }),
          category: "BUSINESS_JET_WANTED",
          categoryLabel: "Business Jet Wanted",
          moduleGroup: "Business Jet",
          titleOf: (x) => `${x.aircraftType?.name ?? "Business Jet"} Wanted`,
          subOf: (x) =>
            `Route: ${x.originAirport?.iataCode ?? "-"} -> ${x.destinationAirport?.iataCode ?? "-"}`,
        },
      ];

      for (const s of sources) {
        try {
          const r = await s.call();
          const data = extractData(r);
          data.forEach((x: any) => {
            results.push({
              id: x.id,
              category: s.category,
              categoryLabel: s.categoryLabel,
              moduleGroup: s.moduleGroup,
              title: s.titleOf(x),
              subTitle: s.subOf?.(x),
              companyName: x.company?.companyName ?? "N/A",
              userName: x.user?.fullName ?? x.user?.email,
              createdDate: x.createdDate,
              raw: x,
            });
          });
        } catch (e) {
          console.error("Expired load error:", s.category, e);
        }
      }

      results.sort((a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime());
      setListings(results);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Listing Management",
        items: [{ label: "Expired Listings", route: "/dashboard/expired-listings" }],
      },
    ]);
    loadExpired();
    return () => setPageMenus([]);
  }, [loadExpired, setPageMenus]);

  const callAdminRenew = async (listing: ExpiredListing) => {
    setActionLoading(listing.id);
    try {
      const map: Record<ListingCategory, () => Promise<any>> = {
        AIRCRAFT_SALE_LEASE_AVAILABLE: () => (api as any).aircraftsaleleaseAvailable.adminRenew(listing.id),
        AIRCRAFT_SALE_LEASE_WANTED: () => (api as any).aircraftsaleleaseWanted.adminRenew(listing.id),
        WET_LEASE_AVAILABLE: () => (api as any).wetLeaseAvailable.adminRenew(listing.id),
        WET_LEASE_WANTED: () => (api as any).wetLeaseWanted.adminRenew(listing.id),
        ENGINE_AVAILABLE: () => (api as any).engineSaleLeaseAvailable.adminRenew(listing.id),
        ENGINE_WANTED: () => (api as any).engineSaleLeaseWanted.adminRenew(listing.id),
        CHARTER_AVAILABLE: () => (api as any).aircraftCharterAvailable.adminRenew(listing.id),
        CHARTER_WANTED: () => (api as any).aircraftCharterWanted.adminRenew(listing.id),
        BUSINESS_JET_AVAILABLE: () => (api as any).businessJetAvailable.adminRenew(listing.id),
        BUSINESS_JET_WANTED: () => (api as any).businessJetWanted.adminRenew(listing.id),
      };
      await map[listing.category]();
      setConfirmRenew(null);
      await loadExpired();
    } catch (e) {
      console.error("Admin renew failed", e);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredListings = useMemo(() => {
    if (selectedCategory === "ALL") return listings;
    return listings.filter((x) => x.moduleGroup === selectedCategory);
  }, [listings, selectedCategory]);

  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {
      ALL: listings.length,
      Aircraft: 0,
      "Wet Lease": 0,
      Engine: 0,
      Charter: 0,
      "Business Jet": 0,
    };
    listings.forEach((l) => {
      if (counts[l.moduleGroup] !== undefined) counts[l.moduleGroup]++;
    });
    return counts;
  }, [listings]);

  const columns = useMemo(
    () => [
      {
        key: "moduleGroup",
        title: "Category",
        minWidth: 140,
        accessor: (r: ExpiredListing) => r.moduleGroup,
        render: (r: ExpiredListing) => {
          const colors: Record<string, { bg: string; color: string }> = {
            Aircraft: { bg: "#EFF6FF", color: "#1D4ED8" },
            "Wet Lease": { bg: "#FEF3C7", color: "#B45309" },
            Engine: { bg: "#F3E8FF", color: "#7E22CE" },
            Charter: { bg: "#ECFDF5", color: "#047857" },
            "Business Jet": { bg: "#FDF2F8", color: "#BE185D" },
          };
          const st = colors[r.moduleGroup] || { bg: "#F1F5F9", color: "#475569" };
          return (
            <span
              style={{
                background: st.bg,
                color: st.color,
                padding: "4px 10px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                display: "inline-block",
              }}
            >
              {r.categoryLabel}
            </span>
          );
        },
      },
      {
        key: "title",
        title: "Listing",
        minWidth: 260,
        accessor: (r: ExpiredListing) => r.title,
        render: (r: ExpiredListing) => (
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>{r.title}</div>
            {r.subTitle && <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{r.subTitle}</div>}
          </div>
        ),
      },
      {
        key: "company",
        title: "Company",
        minWidth: 180,
        accessor: (r: ExpiredListing) => r.companyName,
        render: (r: ExpiredListing) => (
          <div>
            <div style={{ fontWeight: 600, color: "#1E293B" }}>{r.companyName}</div>
            {r.userName && <div style={{ fontSize: 11, color: "#94A3B8" }}>By: {r.userName}</div>}
          </div>
        ),
      },
      {
        key: "createdDate",
        title: "Published On",
        minWidth: 140,
        accessor: (r: ExpiredListing) => r.createdDate,
        render: (r: ExpiredListing) => {
          if (!r.createdDate) return "-";
          const d = new Date(r.createdDate);
          return (
            <div style={{ fontSize: 12, color: "#334155" }}>
              {d.toLocaleDateString()}
              <div style={{ fontSize: 10, color: "#94A3B8" }}>
                {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          );
        },
      },
      {
        key: "status",
        title: "Status",
        minWidth: 100,
        render: () => <Badge label="Expired" color="#DC2626" bg="#FEE2E2" />,
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 200,
        render: (r: ExpiredListing) => (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Button icon={MdVisibility} variant="ghost" onClick={() => setSelectedDetail(r)}>
              View
            </Button>
            <Button
              icon={MdAutorenew}
              loading={actionLoading === r.id}
              onClick={() => setConfirmRenew(r)}
              style={{ background: "#F59E0B", color: "#fff" }}
            >
              Renew (Admin)
            </Button>
          </div>
        ),
      },
    ],
    [actionLoading]
  );

  return (
    <div style={{ padding: "32px 40px", background: "#F8FAFC", minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#0F172A", display: "flex", alignItems: "center", gap: 12 }}>
            <span>Expired Listings</span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                background: "#DC2626",
                color: "#fff",
                padding: "2px 10px",
                borderRadius: 20,
              }}
            >
              {listings.length}
            </span>
          </div>
          <div style={{ fontSize: 14, color: "#64748B", marginTop: 4 }}>
            Admin renewal extends an expired listing for 2 more months without consuming the owner's renewal quota.
          </div>
        </div>
        <Button icon={MdRefresh} onClick={loadExpired} loading={loading}>
          Refresh
        </Button>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { key: "ALL", label: "All Expired" },
          { key: "Aircraft", label: "Aircraft Sale/Lease" },
          { key: "Wet Lease", label: "Wet Lease" },
          { key: "Engine", label: "Engine" },
          { key: "Charter", label: "Charter" },
          { key: "Business Jet", label: "Business Jet" },
        ].map((tab) => {
          const isSelected = selectedCategory === tab.key;
          const count = groupCounts[tab.key] || 0;
          return (
            <button
              key={tab.key}
              onClick={() => setSelectedCategory(tab.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 700,
                border: isSelected ? "2px solid #2563EB" : "1px solid #E2E8F0",
                background: isSelected ? "#EFF6FF" : "#FFFFFF",
                color: isSelected ? "#1D4ED8" : "#475569",
                cursor: "pointer",
              }}
            >
              <span>{tab.label}</span>
              <span
                style={{
                  background: isSelected ? "#2563EB" : "#E2E8F0",
                  color: isSelected ? "#FFFFFF" : "#64748B",
                  fontSize: 11,
                  padding: "1px 7px",
                  borderRadius: 10,
                  fontWeight: 800,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #E2E8F0", overflow: "hidden" }}>
        <Table loading={loading} columns={columns} data={filteredListings} />
      </div>

      {confirmRenew && (
        <Modal open onClose={() => setConfirmRenew(null)}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12, color: "#0F172A" }}>
            Renew Listing (Admin)
          </div>
          <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6 }}>
            This will republish <strong>{confirmRenew.title}</strong> (Company:{" "}
            <strong>{confirmRenew.companyName}</strong>) for another 2 months. The owner's renewal quota will{" "}
            <strong>not</strong> be reduced.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
            <Button variant="ghost" onClick={() => setConfirmRenew(null)}>
              Cancel
            </Button>
            <Button
              style={{ background: "#F59E0B", color: "#fff" }}
              icon={MdAutorenew}
              loading={actionLoading === confirmRenew.id}
              onClick={() => callAdminRenew(confirmRenew)}
            >
              Yes, Renew
            </Button>
          </div>
        </Modal>
      )}

      {selectedDetail && (
        <Modal open fullScreen onClose={() => setSelectedDetail(null)}>
          <div style={{ padding: 24 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                borderBottom: "1px solid #E2E8F0",
                paddingBottom: 16,
              }}
            >
              <div>
                <span
                  style={{
                    background: "#FEE2E2",
                    color: "#DC2626",
                    padding: "4px 12px",
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {selectedDetail.categoryLabel} · Expired
                </span>
                <h1 style={{ fontSize: 24, fontWeight: 900, color: "#0F172A", margin: "10px 0 4px 0" }}>
                  {selectedDetail.title}
                </h1>
                <div style={{ color: "#64748B", fontSize: 13 }}>
                  Company: <strong>{selectedDetail.companyName}</strong>
                  {selectedDetail.userName ? ` | By: ${selectedDetail.userName}` : ""}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  icon={MdAutorenew}
                  style={{ background: "#F59E0B", color: "#fff" }}
                  onClick={() => {
                    const l = selectedDetail;
                    setSelectedDetail(null);
                    setConfirmRenew(l);
                  }}
                >
                  Renew (Admin)
                </Button>
              </div>
            </div>

            {/* Spec grid — same pattern as Pending Listings detail view */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
              {Object.entries(selectedDetail.raw)
                .filter(
                  ([key, val]) =>
                    typeof val !== "object" &&
                    val !== null &&
                    val !== undefined &&
                    !["id", "confirmStatus", "isDeleted", "isActive"].includes(key)
                )
                .map(([key, val]) => (
                  <div
                    key={key}
                    style={{
                      background: "#F8FAFC",
                      padding: "12px 16px",
                      borderRadius: 12,
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                      {key}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#1E293B",
                        marginTop: 4,
                        wordBreak: "break-word",
                      }}
                    >
                      {String(val)}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ExpiredListingsPage;
